import type { PreloadedState } from '@reduxjs/toolkit';
import { injectable } from 'inversify';

import PAGES from '@/constants/pages';
import type { RootState } from '@/features/store';
import {
  ConstructorType,
  IConstructorGrid,
  IPossibleConstructorGridItem,
} from '@/interfaces/constructor.interfaces';
import { type ISettings, MediaType } from '@/interfaces/settings.interfaces';
import GridUtils from '@/utils/gridUtils';
import instanceOfGrid from '@/utils/instanceOfGrid';
import mergeGrid from '@/utils/mergeGrid';

import type { IConfigService } from './types';

@injectable()
class ConfigService implements IConfigService {
  _preloadedState: PreloadedState<RootState> = {};

  constructor(preloadedState: PreloadedState<RootState> = {}) {
    this._preloadedState = { ...preloadedState };

    const { config } = this._preloadedState;
    if (!config) {
      throw new Error('Виджет АВН. Не заданы настройки');
    }

    const { element, apiRoot, apiKey, spaceId, envId, booking, media, models, credit } =
      config;

    if (!element) {
      throw new Error('Виджет АВН. Не указан селектор элемента');
    }

    if (!apiRoot) {
      throw new Error('Виджет АВН. Не задан URL для API');
    }

    if (!apiKey) {
      throw new Error('Виджет АВН. Не задан ключ для API');
    }

    if (!spaceId) {
      throw new Error('Виджет АВН. Не задан id пространства для API');
    }

    if (!envId) {
      throw new Error('Виджет АВН. Не задан id окружения для API');
    }

    /**
     * Флаг подтверждающий использования функционал с media.
     * 1. Используется media для изображений
     * 2. Включена страница с моделями
     * 3. Используется чекбокс с изображениями моделей на странице авто
     */
    const isUsedMedia =
      media.type === MediaType.Media ||
      !!models?.enable ||
      !!(
        config.constructor?.[PAGES.Cars] &&
        !!GridUtils.findByCondition(
          mergeGrid(config.constructor?.[PAGES.Cars] as IConstructorGrid),
          element => {
            if (instanceOfGrid(element)) {
              return false;
            }
            const item = element as IPossibleConstructorGridItem;
            return (
              item.type === ConstructorType.FilterModelCheckboxGroup && !item.rejected
            );
          },
        )
      );

    if (!media.apiRoot && isUsedMedia) {
      throw new Error('Виджет АВН. Не задан URL для API медиа');
    }

    if (credit) {
      if (!credit.apiRoot && credit?.enable) {
        throw new Error('Виджет АВН. Не задан URL для API кредитного калькулятора');
      }
    }

    if (booking) {
      if (!booking.apiRoot && booking.enable) {
        throw new Error('Виджет АВН. Не задан URL для API формы бронирования авто');
      }

      if (!booking.spaceId) {
        this._preloadedState = {
          ...this._preloadedState,
          config: {
            ...this.preloadedState.config!,
            booking: {
              ...booking,
              spaceId,
            },
          },
        };
      }

      if (!booking.envId) {
        this._preloadedState = {
          ...this._preloadedState,
          config: {
            ...this.preloadedState.config!,
            booking: {
              ...booking,
              envId,
            },
          },
        };
      }
    }
  }

  get settings() {
    const { config } = this.preloadedState;
    return config as ISettings;
  }

  get preloadedState() {
    return this._preloadedState;
  }
}

export default ConfigService;
